import type { VoyageReportKind } from '@felix/domain';
import { Button } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCalendarEvent,
  IconInfoCircle,
  IconLink,
  IconPlus,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './NoonReportScreen.module.css';
import { formatStamp } from './NoonSections.js';
import { type NextDue, splitMinutes } from './next-due.js';
import { SideTitle } from './RailCards.js';
import type { Voyage, VoyageReport } from './types.js';
import type { PendingVoyageStart } from './voyage-start.js';

/**
 * Sefer raporlarında bağlam bildirimleri (ADR 0157 §2, §3, §5): başlatılmamış sefer uyarısı,
 * bağlam çelişkisi ve sıradaki rapor. Hiçbiri kayıt yazmaz; eylemler mevcut yollara gider.
 */

/** Gemi saatiyle "GG.AA SS:DD" (raporun farkıyla). */
export function shortLt(iso: string, tzOffsetMin: number): string {
  const local = new Date(Date.parse(iso) + tzOffsetMin * 60_000).toISOString();
  return `${local.slice(8, 10)}.${local.slice(5, 7)} ${local.slice(11, 16)}`;
}

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * "Sefer başlatılmadı" (ADR 0157 §2, #2262): en yeni bekleyen kalkış ve sayı. Gemide Seferi
 * Başlat, ilk hedefe bağla ve kalkışı aç; ofiste salt okunur not ve sefer talebi.
 */
export function VoyageStartNotice({
  pending,
  onStart,
  onLink,
  onOpen,
  onRequest,
  busy = false,
}: {
  pending: PendingVoyageStart;
  onStart?: (() => void) | null;
  onLink?: ((target: Voyage) => void) | null;
  onOpen?: (() => void) | null;
  /** Ofis: mevcut `create_voyage` talebi (ADR 0083 §5). */
  onRequest?: (() => void) | null;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.context.notStarted.${key}`, options);
  const { departure } = pending;
  const target = pending.targets[0];
  return (
    <div className={classes.notice} data-tone="warning" role="status">
      <IconAlertTriangle size={16} aria-hidden />
      <span>
        {k('text', {
          time: shortLt(departure.reportedAt, departure.tzOffsetMin),
          port: text(departure.data.port) ?? '—',
        })}
        {pending.count > 1 ? ` ${k('more', { count: pending.count - 1 })}` : null}
        {onRequest ? (
          <>
            <br />
            <small>{k('officeNote')}</small>
          </>
        ) : null}
        <span className={classes.noticeActions}>
          {onStart ? (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onStart}>
              {k('start')}
            </Button>
          ) : null}
          {onLink && target ? (
            <Button
              size="xs"
              variant="light"
              loading={busy}
              leftSection={<IconLink size={14} />}
              onClick={() => onLink(target)}
            >
              {k('link', { no: target.voyageNo, count: pending.queue.length })}
            </Button>
          ) : null}
          {onOpen ? (
            <Button size="xs" variant="subtle" onClick={onOpen}>
              {k('open')}
            </Button>
          ) : null}
          {onRequest ? (
            <Button size="xs" variant="light" onClick={onRequest}>
              {k('request')}
            </Button>
          ) : null}
        </span>
      </span>
    </div>
  );
}

/**
 * Sonraki Rapor kartının "Sefer başlatılmadı" yan maddesi (ADR 0157 §5, #2278): kısa metin ve
 * gemide Seferi Başlat. Asıl uyarı ve öbür eylemler ekranın üstündeki `VoyageStartNotice`'tedir.
 */
export function VoyageStartHint({
  pending,
  onStart,
}: {
  pending: PendingVoyageStart;
  onStart: (() => void) | null;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.context.notStarted.${key}`, options);
  const { departure } = pending;
  return (
    <>
      <p className={classes.hint}>
        {k('text', {
          time: shortLt(departure.reportedAt, departure.tzOffsetMin),
          port: text(departure.data.port) ?? '—',
        })}
      </p>
      {onStart ? (
        <Button
          size="xs"
          mt={6}
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={onStart}
        >
          {k('start')}
        </Button>
      ) : null}
    </>
  );
}

/**
 * Açık bağlantı son raporun bağlamıyla çelişiyor (ADR 0157 §3, #2265): engellemez, iki seçenek.
 */
export function ContextMismatchNotice({
  last,
  lastVoyageNo,
  targetVoyageNo,
  onFollow,
  onStay,
}: {
  last: VoyageReport;
  /** Son raporun seferi; sefersizse `null`. */
  lastVoyageNo: string | null;
  /** Bu ekranın yazacağı sefer; sefersizse `null`. */
  targetVoyageNo: string | null;
  onFollow: () => void;
  onStay: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.context.mismatch.${key}`, options);
  return (
    <div className={classes.notice} data-tone="info" role="status">
      <IconInfoCircle size={16} aria-hidden />
      <span>
        {k(targetVoyageNo ? 'text' : 'textUnlinked', {
          kind: t(`voyage.kinds.${last.kind}`),
          time: shortLt(last.reportedAt, last.tzOffsetMin),
          where: lastVoyageNo ? k('inVoyage', { no: lastVoyageNo }) : k('unlinked'),
          target: targetVoyageNo,
        })}
        <span className={classes.noticeActions}>
          <Button size="xs" variant="light" onClick={onFollow}>
            {k('follow')}
          </Button>
          <Button size="xs" variant="subtle" onClick={onStay}>
            {k('stay')}
          </Button>
        </span>
      </span>
    </div>
  );
}

/** Sıradaki rapor satırı (ADR 0157 §5): tür, zaman (LT ve UTC), kalan süre ya da gecikme. */
export function NextDueText({ due }: { due: NextDue }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.context.due.${key}`, options);
  const kind = (value: VoyageReportKind) => t(`voyage.kinds.${value}`);
  const parts = [k('next', { kind: kind(due.primary) })];
  if (due.dueAt) {
    parts.push(
      k('at', { local: shortLt(due.dueAt, due.tzOffsetMin), utc: formatStamp(due.dueAt) }),
    );
  }
  if (due.remainingMin !== null) {
    parts.push(k(due.remainingMin < 0 ? 'late' : 'remaining', splitMinutes(due.remainingMin)));
  }
  if (due.others.length > 0) parts.push(k('or', { others: due.others.map(kind).join(', ') }));
  return (
    <span data-late={due.remainingMin !== null && due.remainingMin < 0 ? true : undefined}>
      {parts.join(' · ')}
    </span>
  );
}

/**
 * "Sonraki Rapor" kartı (G4-27; ADR 0157 §5): sıradaki rapor, zamanı, ETA ve "Aç". Zamanı geldiyse
 * düğme birincil. Yan maddeler (başlatılmamış sefer, Kiraya Giriş önerisi) altta.
 */
export function NextDueCard({
  due,
  eta,
  onOpen,
  onHire,
  extra,
}: {
  due: NextDue | null;
  /** Seferin planlanan varışı (biçimlenmiş); sefersizse `null`. */
  eta: string | null;
  onOpen: ((kind: VoyageReportKind) => void) | null;
  onHire: boolean;
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.context.due.${key}`, options);
  const target = due?.draft?.kind ?? due?.primary ?? null;
  const late = due?.remainingMin !== null && (due?.remainingMin ?? 1) <= 0;
  return (
    <section className={classes.sideCard} aria-label={k('title')}>
      <SideTitle icon={<IconCalendarEvent size={22} />} title={k('title')} />
      <dl className={classes.stackFacts}>
        <div>
          <dt>{k('title')}</dt>
          <dd>
            {due ? (
              due.draft ? (
                k('draft', {
                  kind: t(`voyage.kinds.${due.draft.kind}`),
                  time: shortLt(due.draft.reportedAt, due.draft.tzOffsetMin),
                })
              ) : (
                <NextDueText due={due} />
              )
            ) : (
              k('none')
            )}
          </dd>
        </div>
        {eta ? (
          <div>
            <dt>{k('eta')}</dt>
            <dd>{eta}</dd>
          </div>
        ) : null}
      </dl>
      {onOpen && target ? (
        <Button
          size="xs"
          mt={12}
          variant={late || due?.draft ? 'filled' : 'light'}
          rightSection={<IconArrowRight size={14} />}
          onClick={() => onOpen(target)}
        >
          {k('open', { kind: t(`voyage.kinds.${target}`) })}
        </Button>
      ) : null}
      {onHire ? <p className={classes.hint}>{t('voyage.context.charter.onHire')}</p> : null}
      {extra}
    </section>
  );
}
